"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HyperFormulaEngine = void 0;
const hyperformula_1 = require("hyperformula");
const moment_1 = __importDefault(require("moment"));
const datedif_plugin_1 = require("./datedif.plugin");
const not_supported_formulas_list_1 = require("./not-supported-formulas-list");
const timedif_plugin_1 = require("./timedif.plugin");
const field_kinds_enum_1 = require("../../../models/fields/field-kinds.enum");
const table_cell_kinds_enum_1 = require("../../../models/tables/table-cell-kinds.enum");
hyperformula_1.HyperFormula.registerFunctionPlugin(datedif_plugin_1.DateDifHF, datedif_plugin_1.DateDifTranslations);
hyperformula_1.HyperFormula.registerFunctionPlugin(timedif_plugin_1.TimeDifPlugin, timedif_plugin_1.TimeDifTranslations);
not_supported_formulas_list_1.NotSupportedFormulas.forEach(hyperformula_1.HyperFormula.unregisterFunction);
class HyperFormulaEngine {
    constructor() {
        this.licenseKey = '46a52-1a0b1-e6ed4-1a930-fca2e';
        this.isoDateFormats = ['YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DDTHH:mm:ss.SSSZ'];
    }
    /**
     * Builds an empty engine instance.
     *
     * @example
     * ```js
     * // build with no initial data
     * const hfInstance = hyperFormulaEngine.initializeFormula();
     * ```
     * @returns HyperFormula instance
     */
    initializeFormula() {
        return hyperformula_1.HyperFormula.buildEmpty({
            licenseKey: this.licenseKey,
            parseDateTime: this.parseDateTime.bind(this),
            dateFormats: [...this.isoDateFormats, ...hyperformula_1.HyperFormula.defaultConfig.dateFormats],
        });
    }
    /**
     * Destroys instance of HyperFormula.
     *
     * @param {HyperFormula} hf - hyperformula instance
     *
     * @example
     * ```js
     * // destroys the instance
     * hyperFormulaEngine.destroyFormula(hfInstance);
     * ```
     */
    destroyFormula(hf) {
        hf.destroy();
    }
    /**
     * Adds a new sheet with the specified name to a HyperFormula instance.
     *
     *    @param {string} name - The name of the sheet to be added.
     *    @param {HyperFormula} hf - The HyperFormula instance to which the sheet is to be added.
     *    @returns {Object} - An object containing the name and ID of the newly added sheet.
     *    @property {string} sheetName - The name of the newly added sheet.
     *    @property {number|undefined} sheet - The ID of the newly added sheet, or undefined if the sheet could not be found.
     */
    addSheet(name, hf) {
        const sheetName = hf.addSheet(name);
        const sheet = hf.getSheetId(name);
        return { sheetName, sheet };
    }
    /**
     * Removes an existing sheet with the specified name to a HyperFormula instance.
     *
     *  @param {string} name - The name of the sheet to be added.
     *  @param {HyperFormula} hf - The HyperFormula instance to which the sheet is to be added.
     *  @returns {ExportedChange[]} - List of cells whose values were affected by an operation, together with their absolute addresses and new values.
     */
    removeSheet(name, hf) {
        const sheetId = hf.getSheetId(name);
        if (sheetId === undefined) {
            return [];
        }
        return hf.removeSheet(sheetId);
    }
    /**
     * Sets the content of a sheet to an empty value.
     *
     * @param {number} sheet - The index of the sheet to set the content for.
     * @param {HyperFormula} hf - The instance of HyperFormula.
     * @returns {void}
     */
    setEmptySheetContent(sheet, hf) {
        hf.setSheetContent(sheet, [['']]);
    }
    /**
     * Retrieves the ID of a sheet in a HyperFormula instance with the specified name.
     *
     * @param {string} sheetName - name of the sheet, for which we want to retrieve ID, case-insensitive.
     * @param {HyperFormula} hf - The HyperFormula instance to retrieve the sheet ID from.
     * @returns {number|undefined} - The ID of the sheet, or undefined if the sheet could not be found.
     */
    getSheet(sheetName, hf) {
        return hf.getSheetId(sheetName);
    }
    /**
     * Sets the contents of a cell in a HyperFormula instance to the specified value.
     *
     * @param {HyperFormula} hf - The HyperFormula instance to set the cell content in.
     * @param {TableCellData} field - The data to set as the cell's contents.
     * @param {Object} options - An object containing the column index, row index, and sheet ID of the cell to set.
     * @param {number} options.colIndex - The index of the column containing the cell to set.
     * @param {number} options.rowIndex - The index of the row containing the cell to set.
     * @param {number} options.sheet - The ID of the sheet containing the cell to set.
     */
    setCellContent(hf, field, { colIndex: col, rowIndex: row, sheet }) {
        hf.setCellContents({ col, row, sheet }, this.getDefaultFieldValue(field));
    }
    /**
     * Registers a listener function to be called whenever values are updated in a HyperFormula instance.
     *
     * @param {HyperFormula} hf - The HyperFormula instance to register the listener with.
     * @param {function} handler - The function to be called when values are updated.
     */
    setValuesUpdateListener(hf, handler) {
        hf.on('valuesUpdated', handler);
    }
    /**
     * Initializes the contents of a sheet in a HyperFormula instance with data from a table.
     *
     * @param {number} sheet - The ID of the sheet to initialize the contents for.
     * @param {Field} table - The table data to use for initializing the sheet.
     * @param {HyperFormula} hf - The HyperFormula instance to initialize the sheet content in.
     * @returns {ExportedChange[]} - An array of objects describing the changes that were made to the sheet.
     */
    initSheetContent(sheet, table, hf) {
        const tableTypes = {
            [field_kinds_enum_1.FieldKinds.Table]: { getRowValues: this.getDefaultTableInitValues.bind(this) },
            [field_kinds_enum_1.FieldKinds.PrefilledTable]: { getRowValues: this.getPrefilledRowValues.bind(this) },
        };
        const rowsValues = tableTypes[table.kind].getRowValues({
            table,
            rows: table.rows,
            hf,
            sheet,
        });
        return hf.setSheetContent(sheet, rowsValues);
    }
    /**
     * Calculates the contents of a sheet in a HyperFormula instance with data from a table.
     *
     * @param {number} sheet - The ID of the sheet to calculate the contents for.
     * @param {Field} table - The table data to use for calculating the sheet contents.
     * @param {HyperFormula} hf - The HyperFormula instance to calculate the sheet content in.
     * @returns {ExportedChange[]} - An array of objects describing the changes that were made to the sheet.
     */
    calculateSheetContent(sheet, table, hf) {
        const tableTypes = {
            [field_kinds_enum_1.FieldKinds.Table]: { getRowValues: this.getDefaultTableRowValues.bind(this) },
            [field_kinds_enum_1.FieldKinds.PrefilledTable]: { getRowValues: this.getPrefilledRowValues.bind(this) },
        };
        const tableKind = table.kind;
        const rowsValues = tableTypes[tableKind].getRowValues({ table, rows: table.rows, hf, sheet });
        return hf.setSheetContent(sheet, rowsValues);
    }
    getDefaultTableInitValues({ table, rows = [], hf, sheet }) {
        const firstRowValue = (rows === null || rows === void 0 ? void 0 : rows.length)
            ? rows[0].columns.map((field, colIndex) => field.kind === table_cell_kinds_enum_1.TableCellKinds.Formula ? table.columns[colIndex].formula : field.value)
            : [];
        hf.setSheetContent(sheet, [firstRowValue]);
        return this.getDefaultTableRowValues({ table, rows, hf, sheet });
    }
    getDefaultTableRowValues({ table, rows = [], hf, sheet }) {
        return rows.map(({ columns }, rowIndex) => columns.map((field, colIndex) => field.kind === table_cell_kinds_enum_1.TableCellKinds.Formula
            ? this.getDefaultTableRowFormula({
                hf,
                formula: table.columns[colIndex].formula,
                colIndex,
                rowIndex,
                sheet,
            })
            : this.getDefaultFieldValue(field)));
    }
    getDefaultTableRowFormula({ hf, formula, colIndex, rowIndex, sheet }) {
        return rowIndex === 0 ? formula : this.translateFormula({ hf, colIndex, targetRow: rowIndex, sheet });
    }
    translateFormula({ hf, colIndex, targetRow, sheet }) {
        const sourceCell = { sheet, row: 0, col: colIndex };
        const targetCell = { sheet, row: targetRow, col: colIndex };
        const [[translated]] = hf.getFillRangeData({ start: sourceCell, end: sourceCell }, { start: targetCell, end: targetCell });
        return translated;
    }
    getPrefilledRowValues({ rows = [] }) {
        return rows.map(({ columns }) => columns.map((field) => field.kind === table_cell_kinds_enum_1.TableCellKinds.Formula ? field.formula : this.getDefaultFieldValue(field)));
    }
    parseDateTime(dateTimeString, dateFormat, timeFormat) {
        if (this.isoDateFormats.includes(dateFormat)) {
            return this.getDateTimeFromISOFormat(dateTimeString, dateFormat);
        }
        return hyperformula_1.HyperFormula.defaultConfig.parseDateTime(dateTimeString, dateFormat, timeFormat);
    }
    getDateTimeFromISOFormat(dateTimeString, dateFormat) {
        const momentDate = (0, moment_1.default)(dateTimeString, dateFormat, true);
        if (momentDate.isValid()) {
            return {
                year: momentDate.year(),
                month: momentDate.month() + 1,
                day: momentDate.date(),
                hours: momentDate.hours(),
                minutes: momentDate.minutes(),
                seconds: momentDate.seconds(),
            };
        }
        return undefined;
    }
    getDefaultFieldValue(field) {
        if (!Object.prototype.hasOwnProperty.call(field, 'value') || field.value === '') {
            return null;
        }
        return field.value;
    }
}
exports.HyperFormulaEngine = HyperFormulaEngine;
